import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';

import { asyncForEach, showError } from '@utils';
import { LIMIT } from '@constants';
import { selectServicePointsPinnedFilters } from '@store';
import { getServicePointsRequestPayload } from '@helpers';
import { QUERY_KEYS } from 'constants/queryKeys';
import { servicePointsServices } from '@services';
import { handleEnums, handleServicePointsData } from './utils';

export const SERVICE_POINTS_PAGE_SIZE = LIMIT;

export const useServicePointsOperations = (overrides = {}) => {
  const { query, isReady } = useRouter();
  const queryClient = useQueryClient();

  const pinnedFilters = useSelector(selectServicePointsPinnedFilters);

  const invalidateServicePoints = useCallback(() => {
    queryClient.invalidateQueries(QUERY_KEYS.GET_SERVICE_POINTS_KEY);
  }, [queryClient]);

  const { viewMode, ...filteredQuery } = query;

  const {
    page = 1,
    pageSize = SERVICE_POINTS_PAGE_SIZE,
    orderBy = 'internalId',
    order = 'asc',
    token,
    ...others
  } = { ...filteredQuery, ...overrides };

  const _otherQueries = Object.entries(others)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const servicePointsResponse = useQuery(
    [
      QUERY_KEYS.GET_SERVICE_POINTS_KEY,
      {
        page,
        pageSize,
        orderBy,
        order,
        _otherQueries,
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      },
    ],
    () => {
      const payload = getServicePointsRequestPayload({
        ...others,
      });
      return servicePointsServices?.getServicePointsFilters({
        page,
        pageSize: parseInt(pageSize, 10) || SERVICE_POINTS_PAGE_SIZE,
        orderBy,
        order,
        query: others && others?.query ? others?.query?.trim() : null,
        ...payload,
      });
    },
    { enabled: isReady }
  );

  const servicePoints = handleServicePointsData(
    servicePointsResponse?.data?.data || []
  );

  const _deleteServicePoints = useCallback(
    async (servicePointsIds) => {
      await asyncForEach(servicePointsIds, async (servicePointId) => {
        await servicePointsServices?.deleteServicePoint(servicePointId);
      });
      invalidateServicePoints();
      await servicePointsResponse.refetch();
    },
    [servicePointsResponse] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const servicePointsEnumsResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_POINTS_ENUMS_KEY],
    () => servicePointsServices?.getServicePointsEnums(),
    { enabled: isReady }
  );

  const _activateDeactivateServicePoints = useCallback(
    async (payload) => {
      await servicePointsServices?.activateDeactivateServicePoints(payload);
      invalidateServicePoints();
      servicePointsResponse.refetch();
    },
    [servicePointsResponse] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const enums = handleEnums(servicePointsEnumsResponse?.data?.data || {});

  const hasFilters =
    pinnedFilters?.length > 0 || Object.keys(others).length > 0;

  useEffect(() => {
    if (servicePointsResponse.error || servicePointsEnumsResponse.error) {
      showError(
        servicePointsResponse.error || servicePointsEnumsResponse.error
      );
    }
  }, [servicePointsResponse.error, servicePointsEnumsResponse.error]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || SERVICE_POINTS_PAGE_SIZE,
    total: servicePointsResponse?.data?.meta?.total,
    loading: servicePointsResponse?.isLoading,
    servicePoints,
    hasFilters,
    enums,
    getServicePoints: servicePointsResponse.refetch,
    deleteServicePoints: _deleteServicePoints,
    activateDeactivateServicePoints: _activateDeactivateServicePoints,
  };
};
